import { z } from 'zod';
import { ActorSchema } from '../common/actor.schema';
import { LANDIQ_BOARD_STAGES } from '../admin/landiq-data-runs.schemas';

// Published when LandIQ accepted an operator's start, from the page or the admin MCP. Audit-only:
// the one fact LandIQ cannot know is which operator started the run. Refusals are not published.
export const LandIqDataRunStartedPayloadSchema = z.object({
  actor: ActorSchema,
  entryId: z.string().min(1),
  runId: z.string().min(1),
  environment: z.string(),
  occurredAt: z.iso.datetime(),
});

export type LandIqDataRunStartedPayload = z.infer<typeof LandIqDataRunStartedPayloadSchema>;

// Published when LandIQ accepted an operator's retry of an import's failed chunks. Audit-only, like a start:
// which operator did it, and how many chunks LandIQ moved back (0 on an idempotent repeat).
export const LandIqDataRunFailedChunksRetriedPayloadSchema = z.object({
  actor: ActorSchema,
  entryId: z.string().min(1),
  runId: z.string().min(1),
  environment: z.string(),
  movedChunks: z.number().int().nonnegative(),
  occurredAt: z.iso.datetime(),
});

export type LandIqDataRunFailedChunksRetriedPayload = z.infer<typeof LandIqDataRunFailedChunksRetriedPayloadSchema>;

// Published by the LandIQ push watcher when every entry of a ticket's stage reached DONE between two
// board reads. Its one effect is the operators' push; LandIQ's board is the record.
export const LandIqStageFinishedPayloadSchema = z.object({
  ticket: z.string().min(1),
  stage: z.enum(LANDIQ_BOARD_STAGES),
  environment: z.string(),
  occurredAt: z.iso.datetime(),
});

export type LandIqStageFinishedPayload = z.infer<typeof LandIqStageFinishedPayloadSchema>;

// Published by the LandIQ push watcher when an entry seen before as not FAILED is FAILED on the
// next board read — a source outage included. Its one effect is the operators' push; LandIQ's board is the record.
export const LandIqDataRunFailedPayloadSchema = z.object({
  ticket: z.string().min(1),
  entryId: z.string().min(1),
  stage: z.enum(LANDIQ_BOARD_STAGES),
  environment: z.string(),
  occurredAt: z.iso.datetime(),
});

export type LandIqDataRunFailedPayload = z.infer<typeof LandIqDataRunFailedPayloadSchema>;

// Published by the LandIQ push watcher when a RUNNING run's progress snapshot first becomes more than 20 minutes
// old. Its one effect is the operators' push; LandIQ's run list is the record.
export const LandIqDataRunStalePayloadSchema = z.object({
  ticket: z.string().min(1),
  entryId: z.string().min(1),
  runId: z.string().min(1),
  stage: z.enum(LANDIQ_BOARD_STAGES),
  environment: z.string(),
  occurredAt: z.iso.datetime(),
});

export type LandIqDataRunStalePayload = z.infer<typeof LandIqDataRunStalePayloadSchema>;
